<template>
  <MarketCard
    :class="[classes.cardWrapper, 'list']"
    style="position: relative;"
  >
    <CardHeader :eventId="eventId" />

    <EventName
      :class="[classes.eventName, 'event-name']"
      :marketId="marketId"
      :eventId="eventId"
      :useTeamNames="true"
    />
    <div :class="classes.historyWrapper">
      <template v-if="showMatchHistory">
        <MatchHistory
          :eventId="eventId"
          :class="classes.history"
          :history="form[0]"
        />
        <div :class="classes.divider" />
        <MatchHistory
          :eventId="eventId"
          :class="classes.history"
          :history="form[1]"
        />
      </template>
      <template v-else>
        <div :class="classes.wlContainer">
          <DynamicText
            :class="[classes.wl, classes.wlLeft]"
            tag="p"
            styleGroup="Event Tag"
            :get="mainTextStyle.get"
            :set="mainTextStyle.set"
            >{{ record[0].W }}-{{ record[0].L }}</DynamicText
          >
          <DynamicText
            :class="[classes.wlSub]"
            tag="p"
            styleGroup="Event Tag Subtext"
            :get="mainTextStyle.get"
            :set="mainTextStyle.set"
            >{{ wlText }}</DynamicText
          >
          <DynamicText
            :class="[classes.wl, classes.wlRight]"
            tag="p"
            styleGroup="Event Tag"
            :get="mainTextStyle.get"
            :set="mainTextStyle.set"
            >{{ record[1].W }}-{{ record[1].L }}</DynamicText
          >
        </div>
      </template>
    </div>
    <div
      :class="[classes.statistics, layoutClass]"
      @scroll="() => handleScroll()"
    >
      <MarketTags
        :class="classes.tabs"
        :subText="subText"
        :tags="[competition]"
      />

      <template v-for="(heading, i) in headings" :key="heading">
        <div v-if="heading" :class="classes.statHeadingWrapper">
          <div :class="classes.spacerLine" />
          <DynamicText
            :class="classes.statHeading"
            tag="span"
            styleGroup="Statistics Text"
            :get="textStyle.get"
            :set="textStyle.set"
          >
            {{ heading }}
          </DynamicText>
          <div :class="classes.spacerLine" />
        </div>

        <MatchStatistics :statistics="data[i]" />
      </template>
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, ComputedRef, defineComponent } from "vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import EventName from "../../../EventNameBadges.vue";
import CardHeader from "../../../CardHeader.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import MarketTags from "../../../MarketTags.vue";
import MatchHistory from "../../../MatchHistory.vue";
import MatchStatistics from "../../../MatchStatistics.vue";
import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { useHandleAnalytics } from "../../../../lib/analytics";
import { SportCompetitor, SportEvent } from "@fresh8/types/src/betData";
import { SportEnum } from "@fresh8/types/src/language";
import DynamicText from "../../../DynamicText.vue";
import { Brand } from "@fresh8/types";
import { useH2HStatistics } from "../../../../hooks/useH2HStatistics";

const useStyles = createUseStyles({
  cardWrapper: {
    flex: "1 1 1px",
    flexDirection: "column",
    flexWrap: "wrap"
  },
  eventName: {
    marginTop: "8px",
    alignSelf: "center",

    "& .competitors .banner": {
      maxWidth: "180px",
      "&.right": {
        marginLeft: props => (props.isCarousel ? "0" : "15px"),
        paddingRight: "8px"
      },
      "&.left": {
        marginRight: props => (props.isCarousel ? "0" : "15px"),
        paddingLeft: "8px"
      }
    },
    "& .vs": {
      placeSelf: "center",
      width: "25px"
    }
  },
  historyWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    borderBottom: props => `1px solid ${props.marketBorderColor || "#dcddf"}`
  },
  history: {
    paddingTop: "7px",
    paddingBottom: "5px"
  },
  divider: {
    width: "1px",
    background: props => props.marketBorderColor
  },
  statistics: {
    overflowX: "hidden",
    overflowY: "scroll",
    width: "100%",
    boxSizing: "border-box",
    position: "relative",
    display: "grid",
    padding: "0 6px",
    gridAutoFlow: "row",
    flex: "1",
    [`&.billboard`]: {
      maxHeight: "110px"
    },
    [`&.mpu`]: {
      maxHeight: "118px"
    },
    [`&.large-responsive`]: {
      maxHeight: "136px"
    }
  },
  tabs: {
    paddingBottom: "3px !important",
    marginBottom: "4px"
  },
  spacer: {
    height: "3px"
  },

  wlContainer: {
    display: "flex",
    padding: "4px 0",
    gap: "6px",
    alignItems: "center"
  },
  wl: {
    fontSize: "10px",
    lineHeight: "12px"
  },
  wlLeft: {
    textAlign: "right"
  },
  wlRight: {
    textAlign: "left"
  },
  wlSub: {
    fontSize: "8px"
  },
  statHeadingWrapper: {
    display: "flex",
    alignItems: "center",
    marginTop: "-6px"
  },
  statHeading: {
    fontSize: "12px",
    margin: "8px auto 8px",
    padding: "0 5px"
  },
  spacerLine: {
    background: props => props.marketBorderColor,
    flex: 1,
    height: "1px"
  }
});

export default defineComponent({
  name: "MPU",
  components: {
    CardHeader,
    EventName,
    MarketCard,
    MarketTags,
    MatchHistory,
    MatchStatistics,
    DynamicText
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    let interacted = false;
    const $handleAnalytics = useHandleAnalytics();
    const brandContext = injectBrand();
    const carouselContext = injectCarouselContext();
    const templateSizeContext = injectTemplateSize();
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;
    const backgroundImage = computed(() => {
      return !!Object.keys(brandContext?.backgroundImageSets?.value || {})
        .length;
    });
    const relativeStyles = computed(() => ({
      ...(brandContext?.adTemplateBranding.value || {}),
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);

    const translation = getTranslation(window.navigator.language.toLowerCase());

    const handleScroll = () => {
      if (interacted) return;
      const statsEvent = AnalyticEvents.INTERACTED;
      const statsDesc = "H2H Stats Scroll";

      $handleAnalytics(statsEvent, statsDesc);
      interacted = true;
    };

    const showMatchHistory = computed(
      () => event.value.sport === SportEnum.FOOTBALL
    );

    const statistics = useH2HStatistics(competitors.value);

    return {
      headings: computed(() =>
        statistics.headings.value.map(h =>
          h
            ? getTranslation(window.navigator.language.toLowerCase()).H2HTexts[
                h
              ]
            : undefined
        )
      ),
      data: computed(() => statistics.data.value),
      wlText: computed(() => {
        const w = getTranslation(window.navigator.language.toLowerCase())
          .H2HTexts["W"];
        const l = getTranslation(window.navigator.language.toLowerCase())
          .H2HTexts["L"];
        return `${w}/${l}`;
      }),
      record: computed(() => statistics.meta.value.record),
      form: computed(() => statistics.meta.value.form),
      showMatchHistory,
      handleScroll,
      subText: translation.H2HTexts["Per match"] || "Per match",
      competition: event.value.competition,
      competitors,
      layoutClass: computed(() => templateSizeContext?.size.value.name),
      marketBranding: brandContext?.marketBranding,
      classes: classesRef,
      mainTextStyle: {
        get: (brand: Brand | undefined) =>
          (brand && brand.adTemplate.eventTag?.mainTextFont) ||
          (brand && brand.adTemplate.market.teamNameFont),
        set: (brand: Brand | undefined, value: string) => {
          if (brand) {
            brand.adTemplate.eventTag.mainTextFont = value;
          }
        }
      },
      subTextStyle: {
        get: (brand: Brand | undefined) =>
          (brand && brand.adTemplate.eventTag?.subTextFont) ||
          (brand && brand.adTemplate.market.teamNameFont),
        set: (brand: Brand, value: string) => {
          brand.adTemplate.eventTag.subTextFont = value;
        }
      },
      textStyle: {
        get: (brand: Brand | undefined) => {
          if (brand?.adTemplate?.h2hCard?.statisticTextFont) {
            return brand.adTemplate.h2hCard.statisticTextFont;
          }
        },
        set: (brand: Brand | undefined, value: string) => {
          if (brand?.adTemplate?.h2hCard?.statisticTextFont) {
            brand.adTemplate.h2hCard.statisticTextFont = value;
          }
        }
      }
    };
  }
});
</script>
